import React, { useState, useEffect } from 'react';
import { Activity, Search, Calendar, User, Clock, Eye, Edit, Trash2, FileText, Shield, Download, RefreshCw, AlertTriangle } from 'lucide-react';
import { auditAPI } from '../services/api';

const ManagementAuditTrail = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, critical: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSeverity, setSelectedSeverity] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const getActionIcon = (action) => {
    switch(action.toLowerCase()) {
      case 'login': return <Shield className="h-4 w-4" />;
      case 'view': case 'access': return <Eye className="h-4 w-4" />;
      case 'update': case 'edit': return <Edit className="h-4 w-4" />;
      case 'delete': case 'remove': return <Trash2 className="h-4 w-4" />;
      case 'create': return <FileText className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'info': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'auth': return <Shield className="h-3 w-3" />;
      case 'config': return <Activity className="h-3 w-3" />;
      case 'user_mgmt': return <User className="h-3 w-3" />;
      case 'reports': return <FileText className="h-3 w-3" />;
      case 'access': return <Eye className="h-3 w-3" />;
      default: return <Activity className="h-3 w-3" />;
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // Load audit logs from API
  const loadLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        search: searchTerm,
        category: selectedCategory !== 'all' ? selectedCategory : '',
        severity: selectedSeverity !== 'all' ? selectedSeverity : '',
        page,
        limit: 25
      };
      
      const [logsRes, statsRes] = await Promise.all([
        auditAPI.list(params),
        auditAPI.getStats()
      ]);
      
      setLogs(logsRes.data.logs || []);
      setTotalPages(logsRes.data.pagination?.totalPages || 1);
      setStats(statsRes.data || { total: 0, today: 0, critical: 0 });
      
    } catch (e) {
      setError(e.message || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  // Load logs on component mount and when filters change
  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when filters/page change
  }, [searchTerm, selectedCategory, selectedSeverity, page]);

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Activity className="h-8 w-8 mr-3 text-blue-600" />
              Audit Trail
            </h1>
            <p className="text-gray-600 mt-2">Monitor and track all system activities and user actions</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
                <div className="text-sm text-gray-500">Total Events</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.today}</div>
                <div className="text-sm text-gray-500">Today</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
                <div className="text-sm text-gray-500">Critical</div>
              </div>
            </div>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" 
                    placeholder="Search activities, users, or actions..."
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <select 
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  <option value="auth">Authentication</option>
                  <option value="config">Configuration</option>
                  <option value="user_mgmt">User Management</option>
                  <option value="reports">Reports</option>
                  <option value="access">Access</option>
                </select>
                <select 
                  value={selectedSeverity}
                  onChange={(e) => setSelectedSeverity(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="all">All Severities</option>
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="critical">Critical</option>
                </select>
                <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  <RefreshCw className="h-4 w-4 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Clock className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">Activity Timeline</h2>
              </div>
              <div className="text-sm text-gray-500">
                {logs.length} events found
              </div>
            </div>
          </div>

          <div className="p-6">
            {/* Error State */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="text-red-700 font-medium">{error}</span>
                <button onClick={loadLogs} className="ml-auto text-red-600 hover:text-red-800 text-sm font-medium">
                  Retry
                </button>
              </div>
            )}
            
            {/* Loading State */}
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                <p className="text-gray-500 mt-2">Loading audit logs...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-12">
                <AlertTriangle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">No audit logs found</p>
                <p className="text-gray-400 text-sm">Try adjusting your search filters</p>
              </div>
            ) : (
              <div className="space-y-4">
                {logs.map((log, index) => (
                  <div key={log.id} className="flex items-start space-x-4 p-4 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100">
                    <div className={`p-2 rounded-lg text-white ${log.severity === 'critical' ? 'bg-red-500' : log.severity === 'warning' ? 'bg-yellow-500' : 'bg-emerald-500'}`}>
                      {getActionIcon(log.action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-3">
                          <span className="font-medium text-gray-900">{log.username}</span>
                          <span className="text-gray-600">•</span>
                          <span className="text-gray-600 capitalize font-medium">{log.action}</span>
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getSeverityColor(log.severity)}`}>
                            {getCategoryIcon(log.category)}
                            <span className="ml-1 capitalize">{log.category.replace('_', ' ')}</span>
                          </div>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Calendar className="h-3 w-3 mr-1" />
                          {getTimeAgo(log.timestamp)}
                        </div>
                      </div>
                      <p className="text-gray-600 text-sm">{log.detail}</p>
                      <div className="mt-2 text-xs text-gray-400">
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer with Pagination */}
          <div className="px-6 py-4 border-t border-gray-100">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="text-sm text-gray-500">
                Real-time monitoring • Page {page} of {totalPages}
              </div>
              <div className="flex items-center space-x-4">
                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button 
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1 || loading}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
                    <button 
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages || loading}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                )}
                <button 
                  onClick={loadLogs}
                  disabled={loading}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium disabled:opacity-50"
                >
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
                <button className="text-sm text-emerald-600 hover:text-emerald-700 font-medium">
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Today\'s Activities', value: '156', color: 'text-blue-600' },
            { label: 'Failed Logins', value: '3', color: 'text-red-600' },
            { label: 'Config Changes', value: '8', color: 'text-yellow-600' },
            { label: 'User Actions', value: '89', color: 'text-green-600' }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default ManagementAuditTrail;
